/**
 * Admin — Tariffario di rimborso chilometrico (BE v1.100.0, `erp-mileage-rates`).
 *
 * **Perché una tabella e non un numero nelle impostazioni.** Le tabelle ACI cambiano ogni anno: a
 * gennaio entra in vigore la tariffa nuova, ma le rendicontazioni di dicembre devono restare
 * calcolate con quella vecchia. Un valore scalare non ha modo di esprimerlo, e la riga di dicembre
 * riaperta a gennaio verrebbe ricalcolata col numero sbagliato. Per questo ogni tariffa ha una
 * validità, e il server risolve **sulla data della registrazione**, non su oggi.
 *
 * **Le tre specificità** — mezzo singolo → fascia → azienda — sono un ordine, non un'etichetta: il
 * risolutore prova la più stretta e si ferma alla prima che ha candidati. Due tariffe di pari
 * specificità valide nello stesso giorno sono un'ambiguità che il BE rifiuta in scrittura
 * (`422 MILEAGE_RATE_OVERLAP`); qui la si intercetta mentre si sceglie la data, perché scoprirla al
 * salvataggio costa il form intero.
 *
 * Lo strumento di **verifica** in fondo alla pagina risponde alla domanda che si fa davvero chi
 * configura: «con questa data e questo mezzo, quale tariffa si applica?». È un'anteprima calcolata
 * sulle stesse regole del server — l'importo che finisce a bilancio lo scrive comunque il BE.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ActionIcon, Badge, Button, Group, NumberInput, Select, SimpleGrid, Switch, Table, Text,
  Textarea, TextInput, Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconAlertTriangle, IconCurrencyEuro, IconPencil, IconPlus, IconRoute, IconTrash, IconWand,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { oggiIso, toDateOnly } from '@/shared/lib/format';
import { useAbility } from '@/app/providers/AbilityProvider';
import { MaeCard, MaeField, MaeFormSection, MaeModal, MaePageHead, MaeShell } from '@/shared/desktop/MaeShell';
import { maeTableStyles } from '@/shared/desktop/MaeShell.utils';
import { StatCard } from '@/shared/components/StatCard';
import { MaeAsync } from '@/shared/components/MaeAsync';
import { MaeAdvice } from '@/shared/components/MaeAdvice';
import { Paginated } from '@/shared/components/DataTable/TablePagination';
import { useCreateResource, useDeleteResource, useResourceList, useUpdateResource } from '@/shared/api/jsonapi/hooks';
import { notifyError, notifySuccess, getErrorMessage } from '@/shared/lib/notify';
import { mw } from '@/shared/mobile/tokens';
import {
  RATE_SPECIFICITY_LABEL, VEHICLE_CATEGORY_OPTIONS, findOverlappingRate, formatMoney,
  mileageRateHealth, previewReimbursement, rateSpecificity, resolveMileageRate, vehicleCategoryLabel, vehicleLabel,
  type EmployeeVehicleRow, type MileageRateRow,
} from '@/features/fleet/ownVehicle';

export const Route = createFileRoute('/_app/organization/mileage-rates')({ component: MileageRatesPage });

type RateScope = 'tenant' | 'category' | 'vehicle';

const SCOPE_OPTIONS: { value: RateScope; label: string; description: string }[] = [
  { value: 'tenant', label: 'Azienda', description: 'Vale per chiunque non abbia una tariffa più specifica' },
  { value: 'category', label: 'Fascia', description: 'Vale per tutti i mezzi di quella categoria' },
  { value: 'vehicle', label: 'Mezzo singolo', description: 'Vale solo per quel mezzo, e vince su tutto' },
];

interface RateForm {
  scope: RateScope;
  vehicleCategory: string | null;
  employeeVehicleId: string | null;
  ratePerKm: number | string;
  currency: string;
  validFrom: string | null;
  validUntil: string | null;
  isActive: boolean;
  notes: string;
}

const EMPTY: RateForm = {
  scope: 'tenant', vehicleCategory: null, employeeVehicleId: null, ratePerKm: '',
  currency: 'EUR', validFrom: null, validUntil: null, isActive: true, notes: '',
};

function MileageRatesPage() {
  const ability = useAbility();
  const [editing, setEditing] = useState<MileageRateRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const ratesQ = useResourceList<MileageRateRow>('erp-mileage-rates', {
    include: ['employeeVehicle'],
    sort: ['-validFrom'],
    page: { size: 100 },
  });
  const vehiclesQ = useResourceList<EmployeeVehicleRow>('erp-employee-vehicles', {
    page: { size: 100 },
    sort: ['licensePlate'],
  });

  const create = useCreateResource<MileageRateRow>('erp-mileage-rates');
  const update = useUpdateResource<MileageRateRow>('erp-mileage-rates');
  const remove = useDeleteResource('erp-mileage-rates');

  const rates = useMemo(() => ratesQ.data?.data ?? [], [ratesQ.data]);
  const vehicles = useMemo(() => vehiclesQ.data?.data ?? [], [vehiclesQ.data]);
  const canWrite = ability.can('create', 'ErpMileageRate');
  const canUpdate = ability.can('update', 'ErpMileageRate');
  const canDelete = ability.can('delete', 'ErpMileageRate');

  const vehicleOptions = useMemo(
    () => vehicles.map((v) => ({ value: v.id, label: vehicleLabel(v) })),
    [vehicles],
  );

  const activeCount = rates.filter((r) => r.isActive !== false).length;
  /* La stessa regola del pallino nel menu (`MileageTenantRateMissing`, `MileageRateAmbiguous`):
     conta solo una tariffa valida **oggi**, estremi compresi. Fino al 02-11-2026 qui bastava
     «attiva e di livello azienda», e una tariffa scaduta passava per presente. */
  const health = useMemo(() => mileageRateHealth(rates, oggiIso()), [rates]);
  const hasTenantDefault = health.tenantRate !== 'missing';

  const form = useForm<RateForm>({
    initialValues: EMPTY,
    validate: {
      ratePerKm: (v) => (typeof v === 'number' && v > 0 ? null : 'Indica una tariffa maggiore di zero'),
      currency: (v) => (/^[A-Z]{3}$/.test(v) ? null : 'Codice ISO di 3 lettere (es. EUR)'),
      vehicleCategory: (v, values) => (values.scope === 'category' && !v ? 'Scegli la fascia' : null),
      employeeVehicleId: (v, values) => (values.scope === 'vehicle' && !v ? 'Scegli il mezzo' : null),
      validUntil: (v, values) =>
        v && values.validFrom && dayjs(v).isBefore(dayjs(values.validFrom), 'day')
          ? 'La fine validità deve seguire l\'inizio'
          : null,
    },
  });

  // Il 422 `MILEAGE_RATE_OVERLAP` anticipato: lo si vede mentre si sceglie la data, non dopo il
  // salvataggio. Ricalcolato a ogni render del form perché dipende da 5 campi diversi.
  const clash = useMemo(() => {
    if (!modalOpen) return null;
    const rate = typeof form.values.ratePerKm === 'number' ? form.values.ratePerKm : 0;
    if (rate <= 0) return null;
    return findOverlappingRate(rates, {
      id: editing?.id,
      employeeVehicleId: form.values.scope === 'vehicle' ? form.values.employeeVehicleId : null,
      vehicleCategory: form.values.scope === 'category' ? form.values.vehicleCategory : null,
      validFrom: form.values.validFrom ? dayjs(form.values.validFrom).format('YYYY-MM-DD') : null,
      validUntil: form.values.validUntil ? dayjs(form.values.validUntil).format('YYYY-MM-DD') : null,
      isActive: form.values.isActive,
    });
  }, [modalOpen, rates, editing, form.values]);

  function openCreate() {
    setEditing(null);
    form.setValues(EMPTY);
    form.resetDirty(EMPTY);
    setModalOpen(true);
  }

  function openEdit(r: MileageRateRow) {
    setEditing(r);
    const scope: RateScope = r.employeeVehicle?.id ? 'vehicle' : r.vehicleCategory ? 'category' : 'tenant';
    const values: RateForm = {
      scope,
      vehicleCategory: r.vehicleCategory ?? null,
      employeeVehicleId: r.employeeVehicle?.id ?? null,
      ratePerKm: r.ratePerKm ?? '',
      currency: r.currency ?? 'EUR',
      validFrom: toDateOnly(r.validFrom),
      validUntil: toDateOnly(r.validUntil),
      isActive: r.isActive !== false,
      notes: r.notes ?? '',
    };
    form.setValues(values);
    form.resetDirty(values);
    setModalOpen(true);
  }

  async function submit(values: RateForm) {
    const attributes = {
      vehicleCategory: values.scope === 'category' ? values.vehicleCategory : null,
      ratePerKm: typeof values.ratePerKm === 'number' ? values.ratePerKm : 0,
      currency: values.currency.toUpperCase(),
      validFrom: values.validFrom ? dayjs(values.validFrom).format('YYYY-MM-DD') : null,
      validUntil: values.validUntil ? dayjs(values.validUntil).format('YYYY-MM-DD') : null,
      isActive: values.isActive,
      notes: values.notes.trim() || null,
    };
    // `employeeVehicleId` è una FK **senza `[Attr]`** sul BE: passa solo come relationship. Mandarla
    // fra gli attributi darebbe 422 «attribute does not exist».
    const relationships = {
      employeeVehicle: values.scope === 'vehicle' && values.employeeVehicleId
        ? { data: { type: 'erp-employee-vehicles', id: values.employeeVehicleId } }
        : { data: null },
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, attributes, relationships });
        notifySuccess('Tariffa aggiornata');
      } else {
        await create.mutateAsync({ attributes, relationships });
        notifySuccess('Tariffa creata');
      }
      setModalOpen(false);
    } catch (e) {
      notifyError(getErrorMessage(e));
    }
  }

  function confirmDelete(r: MileageRateRow) {
    modals.openConfirmModal({
      title: 'Elimina tariffa',
      children: (
        <Text size="sm">
          Elimini la tariffa da {formatMoney(r.ratePerKm, r.currency ?? 'EUR')}/km? Gli importi già
          calcolati sulle registrazioni restano: sono persistiti sulla riga, non ricalcolati in lettura.
        </Text>
      ),
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await remove.mutateAsync(r.id);
          notifySuccess('Tariffa eliminata');
        } catch (e) {
          notifyError(getErrorMessage(e));
        }
      },
    });
  }

  return (
    <MaeShell>
      <MaePageHead
        title="Tariffario chilometrico"
        sub="Quanto vale un chilometro percorso con il mezzo proprio, e da quando"
        icon={<IconRoute size={23} />}
        actions={canWrite ? <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Nuova tariffa</Button> : undefined}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="lg">
        <StatCard label="Tariffe attive" value={activeCount} icon={IconCurrencyEuro} color="dark" />
        <StatCard label="Tariffe censite" value={rates.length} icon={IconRoute} color="gray" />
        <StatCard
          label="Default aziendale"
          value={health.tenantRate === 'present' ? 'Presente' : health.tenantRate === 'ambiguous' ? 'Doppia' : 'Mancante'}
          icon={health.tenantRate === 'present' ? IconCurrencyEuro : IconAlertTriangle}
          color={health.tenantRate === 'present' ? 'green' : 'orange'}
        />
      </SimpleGrid>

      {!ratesQ.isLoading && !hasTenantDefault && (
        <div style={{ marginBottom: 16 }}>
          {/* Due rami espliciti e non uno `spread` condizionale: il tipo di `MaeAdvice` è un'unione
              che pretende **un** rimedio, ed è quel vincolo a impedire i banner senza via d'uscita. */}
          {canWrite ? (
            <MaeAdvice
              tone="warning"
              title="Nessuna tariffa aziendale valida oggi"
              action={{ label: 'Crea la tariffa aziendale', onClick: openCreate }}
            >
              Senza una tariffa per l’azienda valida oggi, chi rendiconta chilometri con il mezzo proprio li
              registra ma non riceve nessun rimborso: il sistema salva la riga e lascia l’importo vuoto.
            </MaeAdvice>
          ) : (
            <MaeAdvice
              tone="warning"
              title="Nessuna tariffa aziendale valida oggi"
              selfRemedy="Chiedi a chi gestisce il personale di censirla."
            >
              Senza una tariffa per l’azienda valida oggi, chi rendiconta chilometri con il mezzo proprio li
              registra ma non riceve nessun rimborso: il sistema salva la riga e lascia l’importo vuoto.
            </MaeAdvice>
          )}
        </div>
      )}

      {/* Righe scritte prima del controllo in scrittura, o direttamente nel database: il sistema non
          sceglie fra due tariffe pari e il rimborso di chi ci ricade resta vuoto. */}
      {!ratesQ.isLoading && health.ambiguousLevels.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <MaeAdvice
            tone="warning"
            title="Due tariffe valgono insieme oggi"
            selfRemedy={canUpdate
              ? 'Cambia le date di una delle due, o disattivala, dalla tabella qui sotto.'
              : 'Chiedi a chi gestisce il personale di correggerle.'}
          >
            Per {health.ambiguousLevels.map((l) => RATE_SPECIFICITY_LABEL[l].toLowerCase()).join(', ')} ci
            sono due tariffe valide nello stesso giorno: il sistema non ne sceglie una a caso, e chi ci
            ricade registra i chilometri senza rimborso.
          </MaeAdvice>
        </div>
      )}

      <MaeAsync
        query={ratesQ}
        loading="Caricamento del tariffario…"
        empty={rates.length === 0 && {
          icon: IconRoute,
          title: 'Nessuna tariffa censita',
          description: 'Finché il tariffario è vuoto i chilometri si registrano ma non producono rimborso. Comincia dalla tariffa aziendale, poi affina per fascia o per singolo mezzo.',
          ...(canWrite ? { action: { label: 'Nuova tariffa', onClick: openCreate } } : {}),
        }}
      >
        <MaeCard padding={0}>
          <Paginated rows={rates} pageSize={15} label="tariffe" barPad={16}>
            {(page) => (
              <Table.ScrollContainer minWidth={820}>
                <Table striped highlightOnHover verticalSpacing="sm" styles={maeTableStyles}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Si applica a</Table.Th>
                      <Table.Th>Tariffa</Table.Th>
                      <Table.Th>Valida dal</Table.Th>
                      <Table.Th>Valida fino al</Table.Th>
                      <Table.Th>Stato</Table.Th>
                      <Table.Th w={90} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {page.map((r) => {
                      const spec = rateSpecificity(r);
                      return (
                        <Table.Tr key={r.id} style={r.isActive === false ? { opacity: 0.55 } : undefined}>
                          <Table.Td>
                            <Group gap={8} wrap="nowrap">
                              <Badge
                                size="sm"
                                variant="light"
                                color={spec === 'employee-vehicle' ? 'grape' : spec === 'vehicle-category' ? 'blue' : 'gray'}
                              >
                                {RATE_SPECIFICITY_LABEL[spec]}
                              </Badge>
                              <Text size="sm">
                                {spec === 'employee-vehicle'
                                  ? (r.employeeVehicle?.licensePlate ?? r.employeeVehicle?.id ?? '—')
                                  : spec === 'vehicle-category'
                                    ? vehicleCategoryLabel(r.vehicleCategory)
                                    : 'Tutti i mezzi'}
                              </Text>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Text fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {formatMoney(r.ratePerKm, r.currency ?? 'EUR')}
                              <Text component="span" size="xs" c="dimmed" fw={500}> /km</Text>
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c={r.validFrom ? undefined : 'dimmed'}>
                              {r.validFrom ? dayjs(r.validFrom).format('DD/MM/YYYY') : 'Sempre'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c={r.validUntil ? undefined : 'dimmed'}>
                              {r.validUntil ? dayjs(r.validUntil).format('DD/MM/YYYY') : 'In vigore'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge size="sm" variant="light" color={r.isActive === false ? 'gray' : 'green'}>
                              {r.isActive === false ? 'Disattivata' : 'Attiva'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Group gap={4} justify="flex-end" wrap="nowrap">
                              {canUpdate && (
                                <Tooltip label="Modifica">
                                  <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(r)} aria-label="Modifica tariffa">
                                    <IconPencil size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                              {canDelete && (
                                <Tooltip label="Elimina">
                                  <ActionIcon variant="subtle" color="red" onClick={() => confirmDelete(r)} aria-label="Elimina tariffa">
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            )}
          </Paginated>
        </MaeCard>

        <RateSimulator rates={rates} vehicles={vehicles} />
      </MaeAsync>

      <MaeModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Modifica tariffa' : 'Nuova tariffa'}
        icon={<IconRoute size={17} />}
        footer={
          <Button onClick={() => form.onSubmit(submit)()} loading={create.isPending || update.isPending}>
            {editing ? 'Salva' : 'Crea'}
          </Button>
        }
      >
        <MaeFormSection title="A chi si applica" sub="Dalla più stretta alla più larga: se esiste una tariffa per il mezzo, vince su quella di fascia, che vince su quella aziendale.">
          <MaeField label="Specificità">
            <Select
              data={SCOPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              allowDeselect={false}
              value={form.values.scope}
              onChange={(v) => {
                form.setFieldValue('scope', (v as RateScope) ?? 'tenant');
                form.setFieldValue('vehicleCategory', null);
                form.setFieldValue('employeeVehicleId', null);
              }}
            />
          </MaeField>
          <Text size="xs" c="dimmed">
            {SCOPE_OPTIONS.find((o) => o.value === form.values.scope)?.description}
          </Text>
          {form.values.scope === 'category' && (
            <MaeField label="Fascia" required>
              <Select placeholder="Seleziona…" data={[...VEHICLE_CATEGORY_OPTIONS]} {...form.getInputProps('vehicleCategory')} />
            </MaeField>
          )}
          {form.values.scope === 'vehicle' && (
            <MaeField label="Mezzo" required hint={vehicleOptions.length === 0 ? 'Nessun mezzo censito: i dipendenti li aggiungono dalla pagina «Mezzo proprio».' : undefined}>
              <Select placeholder="Seleziona…" data={vehicleOptions} searchable {...form.getInputProps('employeeVehicleId')} />
            </MaeField>
          )}
        </MaeFormSection>

        <MaeFormSection title="Importo">
          <Group grow>
            <MaeField label="Tariffa al km" required>
              <NumberInput
                min={0}
                step={0.01}
                decimalScale={4}
                placeholder="0,25"
                leftSection={<Text size="xs" c="dimmed" pl={4} style={{ fontVariantNumeric: 'tabular-nums' }}>€</Text>}
                {...form.getInputProps('ratePerKm')}
              />
            </MaeField>
            <MaeField label="Valuta" required>
              <TextInput
                maxLength={3}
                {...form.getInputProps('currency')}
                onChange={(e) => form.setFieldValue('currency', e.currentTarget.value.toUpperCase())}
              />
            </MaeField>
          </Group>
        </MaeFormSection>

        <MaeFormSection title="Validità" sub="Vuoto significa «senza estremo», non «da oggi»: la tariffa senza inizio vale anche sulle registrazioni passate.">
          <Group grow>
            <MaeField label="Valida dal">
              <DatePickerInput placeholder="Sempre" valueFormat="DD/MM/YYYY" clearable {...form.getInputProps('validFrom')} />
            </MaeField>
            <MaeField label="Valida fino al">
              <DatePickerInput placeholder="In vigore" valueFormat="DD/MM/YYYY" clearable {...form.getInputProps('validUntil')} />
            </MaeField>
          </Group>
          {clash && (
            <MaeAdvice
              tone="danger"
              title="Si sovrappone a una tariffa già presente"
              selfRemedy={`Chiudi la validità della tariffa da ${formatMoney(clash.ratePerKm, clash.currency ?? 'EUR')}/km (${clash.validFrom ? dayjs(clash.validFrom).format('DD/MM/YYYY') : 'sempre'} → ${clash.validUntil ? dayjs(clash.validUntil).format('DD/MM/YYYY') : 'in vigore'}) prima di aprire questa.`}
            >
              Due tariffe di pari specificità valide negli stessi giorni rendono il rimborso non
              calcolabile: il sistema rifiuterebbe il salvataggio.
            </MaeAdvice>
          )}
          <Switch
            label="Tariffa attiva"
            description="Disattivala per toglierla dal calcolo senza perdere lo storico."
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)}
          />
          <MaeField label="Note">
            <Textarea autosize minRows={2} placeholder="es. Tabella ACI 2026" {...form.getInputProps('notes')} />
          </MaeField>
        </MaeFormSection>
      </MaeModal>
    </MaeShell>
  );
}

/**
 * «Con questa data e questo mezzo, quale tariffa si applica?» — la domanda che si fa chi configura.
 *
 * Le regole sono quelle del server, replicate in `resolveMileageRate`. È dichiaratamente
 * un'**anteprima**: l'importo che finisce sulla registrazione lo calcola e lo persiste il BE, e
 * scriverlo da qui significherebbe avere due sorgenti dello stesso numero.
 */
function RateSimulator({ rates, vehicles }: { rates: MileageRateRow[]; vehicles: EmployeeVehicleRow[] }) {
  const [date, setDate] = useState<string | null>(oggiIso());
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [km, setKm] = useState<number | string>(100);

  const vehicle = vehicles.find((v) => v.id === vehicleId) ?? null;
  const result = date
    ? resolveMileageRate(rates, {
      date: dayjs(date).format('YYYY-MM-DD'),
      employeeVehicleId: vehicleId,
      vehicleCategory: vehicle?.vehicleCategory ?? null,
    })
    : null;

  return (
    <MaeCard style={{ marginTop: 18 }}>
      <Group gap={10} mb={14} wrap="nowrap">
        <IconWand size={18} color={mw.accentText} />
        <div>
          <Text fw={700} size="sm">Verifica quale tariffa si applica</Text>
          <Text size="xs" c="dimmed">
            Anteprima con le stesse regole del sistema. L&apos;importo definitivo lo calcola il sistema al
            salvataggio della registrazione.
          </Text>
        </div>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <MaeField label="Data della registrazione">
          <DatePickerInput valueFormat="DD/MM/YYYY" value={date} onChange={setDate} />
        </MaeField>
        <MaeField label="Mezzo">
          <Select
            placeholder="Nessun mezzo specifico"
            data={vehicles.map((v) => ({ value: v.id, label: vehicleLabel(v) }))}
            value={vehicleId}
            onChange={setVehicleId}
            searchable
            clearable
          />
        </MaeField>
        <MaeField label="Chilometri">
          <NumberInput min={0} value={km} onChange={setKm} rightSection={<Text size="xs" c="dimmed" pr={6}>km</Text>} />
        </MaeField>
      </SimpleGrid>

      <div style={{ marginTop: 16 }}>
        {result === null && (
          <MaeAdvice
            tone="warning"
            title="Nessuna tariffa applicabile a questa data"
            selfRemedy="Aggiungi una tariffa aziendale senza estremi di validità per coprire i casi non previsti."
          >
            I chilometri verrebbero comunque registrati, ma il rimborso resterebbe vuoto. Non è un errore
            per il sistema: è un dato di configurazione mancante.
          </MaeAdvice>
        )}
        {result && 'ambiguous' in result && (
          <MaeAdvice
            tone="danger"
            title="Due tariffe di pari specificità coprono questa data"
            selfRemedy={`Correggi la validità di una delle tariffe di livello «${RATE_SPECIFICITY_LABEL[result.ambiguous]}».`}
          >
            Finché sono entrambe valide il sistema risponde con un conflitto e la registrazione non si salva.
          </MaeAdvice>
        )}
        {result && 'rate' in result && (
          <Group
            justify="space-between"
            wrap="wrap"
            style={{
              background: mw.surface2, border: `1px solid ${mw.line}`, borderRadius: 12,
              padding: '14px 16px', gap: 12,
            }}
          >
            <Group gap={10} wrap="nowrap">
              <Badge size="sm" variant="light" color="blue">{RATE_SPECIFICITY_LABEL[result.specificity]}</Badge>
              <Text size="sm">
                {formatMoney(result.rate.ratePerKm, result.rate.currency ?? 'EUR')}/km
                {result.rate.notes ? ` · ${result.rate.notes}` : ''}
              </Text>
            </Group>
            <Text fw={700} style={{ fontSize: 'var(--fs-h4)', color: mw.brand, fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(
                previewReimbursement(typeof km === 'number' ? km : 0, result.rate.ratePerKm ?? 0),
                result.rate.currency ?? 'EUR',
              )}
            </Text>
          </Group>
        )}
      </div>
    </MaeCard>
  );
}
